/**
 * Detalhamento do crédito da adquirente dentro do extrato de Conta Corrente.
 *
 * Antes disto, o depósito da Cielo era uma linha solta: "Cielo Visa - Venda
 * parcelada", R$ 13.717,62. Não havia como saber quais vendas formaram aquele
 * valor, quanto de taxa foi cobrado, nem se o dinheiro já tinha sido conciliado.
 *
 * Duas regras de honestidade regem esta tela:
 *
 *  1. **fato e palpite não podem ter a mesma cara.** Com o relatório Detalhado,
 *     cada linha é uma transação real e carrega o NSU e o caminho pelo qual
 *     casou. Sem ele, o detalhamento é inferência pelas parcelas previstas — e
 *     isso vem escrito, não subentendido.
 *  2. **o que não fecha aparece.** A conferência no rodapé põe a soma do
 *     detalhamento ao lado do bruto do depósito; divergiu, fica vermelho.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Loader2, ShieldCheck } from 'lucide-react';
import { financialReportsService } from '@services/financialReportsService';
import { formatCurrencyBRL, formatDateBR } from './financialUtils';
import type { CardSettlementDetail, SettlementMatchSource } from '@/types/cardReceivable';

interface CardSettlementDetailRowProps {
  transactionId: number;
  /** Colunas da tabela do extrato, para o detalhe ocupar a linha inteira. */
  colSpan: number;
}

const fromCents = (cents: number): string => formatCurrencyBRL(cents / 100);

/** Datas: sempre dd/MM/aaaa, via o helper compartilhado do módulo. */
const formatDate = (iso: string): string => formatDateBR(iso) || '—';

/**
 * O rótulo diz de quanto o usuário pode duvidar de cada linha: TID e NSU são
 * identificadores da própria adquirente; "pedido" casou pela referência que
 * enviamos; sem casamento, a venda é desconhecida do sistema.
 */
const MATCH_META: Record<SettlementMatchSource, { label: string; title: string; className: string }> = {
  tid: {
    label: 'TID',
    title: 'Casou pelo identificador da transação na Cielo — é o vínculo mais forte',
    className: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  },
  nsu: {
    label: 'NSU',
    title: 'Casou pelo NSU da transação',
    className: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  },
  pedido: {
    label: 'Referência',
    title: 'Casou pela referência do pedido que enviamos à Cielo',
    className: 'bg-sky-50 border-sky-200 text-sky-700',
  },
  nao_identificado: {
    label: 'Sem venda',
    title: 'A Cielo pagou esta transação e o sistema não a conhece — venda anterior ao PaymentHub ou capturada fora dele',
    className: 'bg-amber-50 border-amber-200 text-amber-700',
  },
};

export const CardSettlementDetailRow: React.FC<CardSettlementDetailRowProps> = ({ transactionId, colSpan }) => {
  const [detail, setDetail] = useState<CardSettlementDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const result = await financialReportsService.getCardSettlementDetail(transactionId);
        if (active) setDetail(result);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Erro ao carregar o depósito');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [transactionId]);

  if (loading) {
    return (
      <tr className="bg-slate-50">
        <td colSpan={colSpan} className="py-3 px-6 text-sm text-slate-500">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Carregando o depósito…
          </span>
        </td>
      </tr>
    );
  }

  if (error || !detail) {
    return (
      <tr className="bg-slate-50">
        <td colSpan={colSpan} className="py-3 px-6 text-sm text-red-600">
          {error ?? 'Depósito não encontrado'}
        </td>
      </tr>
    );
  }

  const { settlement, items, from_detail_report: fromDetail, entry_summary: summary } = detail;
  const itemsGross = items.reduce((sum, i) => sum + i.gross_amount, 0);
  // Tolerância de 2 centavos: é o mesmo arredondamento do rateio das parcelas
  // que a conferência já absorve.
  const balanced = Math.abs(itemsGross - settlement.gross_amount) <= 2;
  const feeDiff = summary
    ? summary.fee_difference
    : settlement.identified_installments > 0
      ? settlement.fee_amount - settlement.expected_fee
      : 0;

  return (
    <tr className="bg-slate-50">
      <td colSpan={colSpan} className="py-3 px-6">
        <div className="flex flex-wrap gap-6 text-xs text-slate-600 mb-2">
          <span>
            Depósito de <strong>{formatDate(settlement.settlement_date)}</strong> ·{' '}
            {settlement.brand_label || settlement.brand} · {settlement.kind_label || settlement.kind}
          </span>
          <span>
            Bruto <strong>{fromCents(settlement.gross_amount)}</strong>
          </span>
          <span>
            Taxa <strong className="text-red-600">-{fromCents(settlement.fee_amount)}</strong>
            {feeDiff !== 0 && (
              <span className="ml-1 text-amber-700">
                ({feeDiff > 0 ? '+' : ''}
                {fromCents(feeDiff)} vs. cadastrada)
              </span>
            )}
          </span>
          <span>
            Líquido <strong className="text-emerald-700">{fromCents(settlement.net_amount)}</strong>
          </span>
          <span>{settlement.transactions} transação(ões) na adquirente</span>
        </div>

        {/* De onde vem o detalhamento. Sem isto, inferência e fato ficariam
            indistinguíveis na tela. */}
        <p className={`text-xs mb-3 inline-flex items-center gap-1.5 ${fromDetail ? 'text-emerald-700' : 'text-slate-500'}`}>
          {fromDetail ? <ShieldCheck size={13} /> : <AlertTriangle size={13} />}
          {fromDetail ? (
            <>
              Detalhado por transação, com NSU da própria Cielo
              {summary && (
                <>
                  {' '}
                  — {summary.identified} de {summary.total} identificadas
                  {summary.unidentified > 0 && (
                    <>
                      , <strong>{fromCents(summary.unidentified_gross)}</strong> sem venda identificada
                    </>
                  )}
                  {summary.fee_divergences > 0 && (
                    <>
                      , <strong>{summary.fee_divergences}</strong> com taxa diferente da cadastrada
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              Inferido pelas parcelas previstas para esta data — para identificar venda a venda,
              importe o relatório <strong>Vendas e Recebíveis → Detalhado</strong> deste período.
            </>
          )}
        </p>

        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <th className="text-left font-medium py-1.5">Origem</th>
              <th className="text-left font-medium py-1.5">Parcela</th>
              <th className="text-right font-medium py-1.5">Bruto</th>
              <th className="text-right font-medium py-1.5">Taxa</th>
              <th className="text-right font-medium py-1.5">Líquido</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const match = item.match_source ? MATCH_META[item.match_source] : null;
              return (
                <tr key={item.card_receivable_id || item.nsu || `linha-${idx}`} className="border-b border-slate-100 last:border-0">
                  <td className="py-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.identified && item.order_id ? (
                        <Link
                          to={`/sales-orders/${item.order_id}`}
                          className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                        >
                          {item.reference} <ExternalLink size={11} />
                        </Link>
                      ) : (
                        <span className={item.identified ? 'text-slate-700' : 'text-amber-700'}>
                          {item.reference}
                        </span>
                      )}
                      {match && (
                        <span
                          title={match.title}
                          className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${match.className}`}
                        >
                          {match.label}
                        </span>
                      )}
                    </div>
                    {(item.nsu || item.sale_date) && (
                      <span className="block text-xs text-slate-400">
                        {item.nsu && <>NSU {item.nsu}</>}
                        {item.nsu && item.sale_date && ' · '}
                        {item.sale_date && <>venda em {formatDate(item.sale_date)}</>}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-slate-600">
                    {item.installments ? `${item.installment_number}/${item.installments}` : '—'}
                  </td>
                  <td className="py-1.5 text-right">{fromCents(item.gross_amount)}</td>
                  <td className="py-1.5 text-right text-slate-500">
                    {item.fee_amount ? (
                      <>
                        {fromCents(item.fee_amount)}
                        {item.fee_percent ? (
                          <span className="text-xs text-slate-400"> ({item.fee_percent.toFixed(2)}%)</span>
                        ) : null}
                        {/* A divergência de taxa é por VENDA — o que o relatório
                            Detalhado destravou. */}
                        {!!item.fee_difference && (
                          <span className="block text-xs text-amber-700" title="Diferença entre a taxa cobrada e a cadastrada">
                            {item.fee_difference > 0 ? '+' : ''}
                            {fromCents(item.fee_difference)} vs. cadastrada
                          </span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-1.5 text-right">{item.net_amount ? fromCents(item.net_amount) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* A conferência que não deixa o detalhe mentir. */}
        <p className={`text-xs mt-2 ${balanced ? 'text-slate-500' : 'text-red-600 font-medium'}`}>
          Soma do detalhamento: {fromCents(itemsGross)} · bruto do depósito: {fromCents(settlement.gross_amount)}
          {balanced
            ? ''
            : ' — o detalhamento NÃO fecha com o depósito; confira antes de usar este número.'}
        </p>
        {!fromDetail && settlement.identified_installments === 0 && (
          <p className="text-xs text-amber-700 mt-1">
            Nenhuma venda identificada: este crédito é anterior à agenda de recebíveis, então o
            sistema não sabe quais vendas o compõem.
          </p>
        )}
      </td>
    </tr>
  );
};

export default CardSettlementDetailRow;
